import ReactHtmlParser from "react-html-parser";
import SntWarning from "@cleanup/components/SntWarning";
import StringUtils from "@/utils/StringUtils";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import usePagePermissionCheck from "./usePagePermissionCheck";

interface PagePermissionWarningProps {
  pageName?: string;
  /** Notified after each check: true when the selected org may access the page */
  onAccessResolved?: (hasAccess: boolean) => void;
}

const bold = (text: string): string => "<b>" + text + "</b>";

/**
 * Classic yellow warning banner shown when the selected org's plan lacks the
 * page's required feature. For pages that blank their content instead, use
 * FeatureNotInPlanEmptyState (same check, centered presentation).
 *
 * Wording states the org's configuration rather than asking for an upgrade:
 * this banner only ever renders for SYSTEM/PARTNER logins slicing into someone
 * else's org, so its reader is staff or a partner admin working out why a page
 * is empty - never a buyer. The plan link is a "for more details" pointer, not
 * a call to action. The effect is phrased around the feature rather than the
 * page because one FeatureKey gates several PageNames, and a few pages stay
 * reachable on permission alone (STIC-15987 maintenance).
 */
const PagePermissionWarning: React.FC<PagePermissionWarningProps> = ({
  pageName,
  onAccessResolved,
}) => {
  const { hasAccess, orgName, requiredFeatures, currentPlan, scopeOrgId } =
    usePagePermissionCheck(pageName, onAccessResolved);

  if (hasAccess) {
    return null;
  }

  const features = bold(requiredFeatures.join(", "));
  // Plan clause dropped when basePlanKey resolves to no known plan, so the
  // sentence never reads "is on the  plan"
  const message = currentPlan
    ? StringUtils.replaceStr(
        translate(LanguageKey.org_plan_excludes_feature_key),
        bold(orgName ?? ""),
        bold(currentPlan),
        features
      )
    : StringUtils.replaceStr(
        translate(LanguageKey.org_no_access_to_feature_key),
        bold(orgName ?? ""),
        features
      );

  return (
    <div className="mt-2 mb-2">
      <SntWarning textClassName="d-flex justify-content-center">
        {ReactHtmlParser(
          message +
            ` <a href='/product_plan?org=${scopeOrgId}'>` +
            translate(LanguageKey.platformplan_detail) +
            "</a>"
        )}
      </SntWarning>
    </div>
  );
};

export default PagePermissionWarning;
